import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Skeleton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Domain as DomainIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import ExportToExcelButton from '@crema/core/AppExportExeclFile';

const SendToCustomerModal = ({
  open,
  onClose,
  duplicateData,
  onSend,
  onFetchCustomersByDomain,
  loading = false,
  sendingResults = null,
  domains = [],
  customers = [],
  emailSentHistory = [],
  // SSE Progress tracking props
  allStatusCount = 0,
  doneStatusCount = 0,
  errorStatusCount = 0,
  errorData = [],
}) => {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  // Get error data for report (similar to free-only page)
  const getErrorDataForReport = (errors) => {
    if (!errors || errors.length === 0) return [];
    // Remove customer_id, customer, timestamp, and message from report
    // Keep only lead details (fullName, email, phone, etc.) and error message
    return errors.map((error) => {
      // eslint-disable-next-line no-unused-vars
      const {customer_id, customer, timestamp, message, ...rest} = error;
      return rest;
    });
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedDomain(null);
      setSelectedCustomers([]);
      setActiveStep(0);
    }
  }, [open]);

  const handleDomainChange = async (event, value) => {
    setSelectedDomain(value);
    setSelectedCustomers([]); // Reset customers when domain changes

    // Fetch customers for the selected domain, filtering out already sent customers
    if (value && onFetchCustomersByDomain) {
      await onFetchCustomersByDomain(value.key, duplicateData);
    }
  };

  const handleCustomerChange = (event, value) => {
    setSelectedCustomers(value);
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedDomain) {
      setActiveStep(1);
    } else if (activeStep === 1 && selectedCustomers.length > 0) {
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSend = async () => {
    if (selectedDomain && selectedCustomers.length > 0) {
      const sendData = {
        duplicateId: duplicateData?.id,
        originalDataId: duplicateData?.original_data?.id || duplicateData?.id,
        domain: selectedDomain,
        customers: selectedCustomers,
        duplicateData: duplicateData?.duplicate_data,
      };

      try {
        await onSend(sendData);
        setActiveStep(3);
      } catch (error) {
        console.error('Error sending data:', error);
      }
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant='h6' gutterBottom>
              Select Domain
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mb: 2}}>
              Choose the domain to send the duplicate data from.
              {duplicateData?.status === 'edited' && (
                <Box
                  component='span'
                  sx={{ml: 1, color: 'success.main', fontWeight: 'bold'}}
                >
                  ✓ This is an edited lead - verified unique
                </Box>
              )}
            </Typography>
            <Autocomplete
              fullWidth
              options={domains}
              value={selectedDomain}
              onChange={handleDomainChange}
              getOptionLabel={(option) => option.label || option.name || option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Select Domain'
                  placeholder='Choose a domain...'
                />
              )}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant='h6' gutterBottom>
              Select Customers
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mb: 2}}>
              Choose which customers to send this duplicate data to.
              {duplicateData?.status === 'edited' && (
                <Box
                  component='span'
                  sx={{ml: 1, color: 'success.main', fontWeight: 'bold'}}
                >
                  ✓ All customers available - no duplicates found
                </Box>
              )}
            </Typography>
            <Autocomplete
              fullWidth
              multiple
              options={customers.filter((c) => !c.sent)}
              value={selectedCustomers}
              onChange={handleCustomerChange}
              getOptionLabel={(option) => option.label || option.name || option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Select Customers'
                  placeholder='Choose customers...'
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option.label || option.name || option}
                    {...getTagProps({index})}
                    color='primary'
                    variant='outlined'
                  />
                ))
              }
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant='h6' gutterBottom>
              Confirm Sending
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mb: 2}}>
              Review your selections before sending.
            </Typography>

            <Card variant='outlined' sx={{mb: 2}}>
              <CardContent>
                <Typography variant='subtitle1' gutterBottom>
                  Duplicate Data to Send:
                </Typography>
                <Typography variant='body2'>
                  <strong>Name:</strong>{' '}
                  {duplicateData?.duplicate_data?.fullName}
                </Typography>
                <Typography variant='body2'>
                  <strong>Email:</strong> {duplicateData?.duplicate_data?.email}
                </Typography>
                <Typography variant='body2'>
                  <strong>Phone:</strong> {duplicateData?.duplicate_data?.phone}
                </Typography>
                <Typography variant='body2'>
                  <strong>MPC_4:</strong> {duplicateData?.duplicate_data?.MPC_4}
                </Typography>
              </CardContent>
            </Card>

            <Card variant='outlined' sx={{mb: 2}}>
              <CardContent>
                <Typography variant='subtitle1' gutterBottom>
                  <DomainIcon sx={{mr: 1, verticalAlign: 'middle'}} />
                  Selected Domain:
                </Typography>
                <Typography variant='body2'>
                  {selectedDomain?.label ||
                    selectedDomain?.name ||
                    selectedDomain}
                </Typography>
              </CardContent>
            </Card>

            <Card variant='outlined'>
              <CardContent>
                <Typography variant='subtitle1' gutterBottom>
                  <PersonIcon sx={{mr: 1, verticalAlign: 'middle'}} />
                  Selected Customers ({selectedCustomers.length}):
                </Typography>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  {selectedCustomers.map((customer, index) => (
                    <Chip
                      key={index}
                      label={customer.label || customer.name || customer}
                      color='primary'
                      variant='outlined'
                      size='small'
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant='h6' gutterBottom>
              Sending Results
            </Typography>
            {/* Live Progress Display */}
            {loading && (
              <Box sx={{mb: 3}}>
                <Typography variant='subtitle1' gutterBottom>
                  Sending Progress:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Card variant='outlined'>
                      <CardContent sx={{textAlign: 'center'}}>
                        <Typography variant='h4' color='primary'>
                          {allStatusCount}
                        </Typography>
                        <Typography variant='body2'>Total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant='outlined'>
                      <CardContent sx={{textAlign: 'center'}}>
                        <Typography variant='h4' color='success.main'>
                          {doneStatusCount}
                        </Typography>
                        <Typography variant='body2'>Success</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant='outlined'>
                      <CardContent sx={{textAlign: 'center'}}>
                        <Typography variant='h4' color='error.main'>
                          {errorStatusCount}
                        </Typography>
                        <Typography variant='body2'>Errors</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Error Details - Show loading or data */}
                {(loading || errorData.length > 0) && (
                  <Card variant='outlined' sx={{mt: 2}}>
                    <CardContent>
                      <Typography variant='subtitle1' gutterBottom>
                        Error Details:
                      </Typography>
                      {loading && errorData.length === 0 ? (
                        <Box>
                          {[1, 2, 3].map((i) => (
                            <Box key={i} sx={{mb: 2}}>
                              <Skeleton
                                variant='text'
                                width='60%'
                                height={24}
                              />
                              <Skeleton
                                variant='text'
                                width='100%'
                                height={20}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : errorData.length > 0 ? (
                        <>
                          <List dense>
                            {errorData.map((error, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <ErrorIcon color='error' />
                                </ListItemIcon>
                                <ListItemText
                                  primary={error.customer || 'Unknown Customer'}
                                  secondary={error.error || error.message}
                                />
                              </ListItem>
                            ))}
                          </List>
                          <Box sx={{mt: 2}}>
                            <ExportToExcelButton
                              fileName={`failed_leads_${
                                new Date().toISOString().split('T')[0]
                              }`}
                              json_data={getErrorDataForReport(errorData)}
                            />
                          </Box>
                        </>
                      ) : null}
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}

            {/* Results Card - Always visible when there are results */}
            {sendingResults && (
              <Card variant='outlined' sx={{mt: 2}}>
                <CardContent>
                  <Typography variant='h6' gutterBottom>
                    Sending Results
                  </Typography>

                  {/* Success/Error Alert - Only show final state when loading is complete */}
                  {!loading && sendingResults.success === true ? (
                    <Alert severity='success' sx={{mb: 2}}>
                      {sendingResults.message || `Data sent successfully!`}
                    </Alert>
                  ) : !loading && sendingResults.success === false ? (
                    <Alert severity='error' sx={{mb: 2}}>
                      {sendingResults.message || `Error sending data`}
                    </Alert>
                  ) : (
                    <Alert severity='info' sx={{mb: 2}}>
                      {sendingResults.message || `Sending in progress...`}
                    </Alert>
                  )}

                  {/* Progress Stats */}
                  <Box sx={{mb: 2}}>
                    <Typography variant='body2' color='text.secondary'>
                      Total: {sendingResults.totalSent || 0} | Success:{' '}
                      {sendingResults.successCount || 0} | Errors:{' '}
                      {sendingResults.errorCount || 0}
                    </Typography>
                  </Box>

                  {/* Error Details */}
                  {(loading ||
                    (sendingResults.errors &&
                      sendingResults.errors.length > 0)) && (
                    <Box sx={{mb: 2}}>
                      <Typography variant='subtitle2' gutterBottom>
                        Error Details:
                      </Typography>
                      {loading &&
                      (!sendingResults.errors ||
                        sendingResults.errors.length === 0) ? (
                        <Box>
                          {[1, 2, 3].map((i) => (
                            <Box key={i} sx={{mb: 2}}>
                              <Skeleton
                                variant='text'
                                width='60%'
                                height={24}
                              />
                              <Skeleton
                                variant='text'
                                width='100%'
                                height={20}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : sendingResults.errors &&
                        sendingResults.errors.length > 0 ? (
                        <List dense>
                          {sendingResults.errors.map((error, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <ErrorIcon color='error' />
                              </ListItemIcon>
                              <ListItemText
                                primary={error.customer || 'Unknown Customer'}
                                secondary={
                                  error.message ||
                                  error.error ||
                                  'Unknown error'
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : null}
                    </Box>
                  )}

                  {/* Download Report Button for Errors */}
                  {((sendingResults?.errorCount || 0) > 0 ||
                    (sendingResults?.errors?.length || 0) > 0) && (
                    <Box sx={{mt: 2}}>
                      <ExportToExcelButton
                        fileName={`failed_leads_${
                          new Date().toISOString().split('T')[0]
                        }`}
                        json_data={getErrorDataForReport(
                          sendingResults?.errors &&
                            sendingResults.errors.length > 0
                            ? sendingResults.errors
                            : errorData,
                        )}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Show loading only when no results are available */}
            {loading && !sendingResults && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                <CircularProgress size={24} />
                <Typography>Sending data...</Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const steps = [
    'Select Domain',
    'Select Customers',
    'Confirm Sending',
    'Results',
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {minHeight: '600px'},
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h6'>Send to Customer</Typography>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Edited Lead Prominent Alert */}
        {duplicateData?.status === 'edited' && (
          <Alert
            severity='info'
            sx={{
              mb: 3,
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196f3',
            }}
          >
            <Typography
              variant='h6'
              gutterBottom
              sx={{color: '#1976d2', fontWeight: 'bold'}}
            >
              📝 Edited Lead - Verified & Ready
            </Typography>
            <Typography variant='body1' sx={{fontWeight: 'medium'}}>
              This lead has been edited and thoroughly checked. It is confirmed
              to be unique and not duplicated with any existing customer data.
              You can safely send this lead to any customer in your system.
            </Typography>
          </Alert>
        )}

        {/* Duplicate Data Info */}
        {duplicateData && (
          <Card variant='outlined' sx={{mb: 3}}>
            <CardContent>
              <Typography variant='subtitle1' gutterBottom>
                Duplicate Data Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant='body2'>
                    <strong>Name:</strong>{' '}
                    {duplicateData.duplicate_data?.fullName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2'>
                    <strong>Email:</strong>{' '}
                    {duplicateData.duplicate_data?.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2'>
                    <strong>Phone:</strong>{' '}
                    {duplicateData.duplicate_data?.phone}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2'>
                    <strong>MPC_4:</strong>{' '}
                    {duplicateData.duplicate_data?.MPC_4}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Edited Lead Alert */}
        {duplicateData?.status === 'edited' && (
          <Alert severity='success' sx={{mb: 3}}>
            <Typography variant='subtitle2' gutterBottom>
              ✅ Edited Lead - Ready to Send
            </Typography>
            <Typography variant='body2'>
              This lead has been edited and checked. It is not duplicated with
              any customer and can be sent to any customer you have. The system
              has verified that the edited information is unique and available
              for distribution.
            </Typography>
          </Alert>
        )}

        {/* Sent History */}
        {emailSentHistory && emailSentHistory.length > 0 && (
          <Card variant='outlined' sx={{mb: 3}}>
            <CardContent>
              <Typography variant='subtitle1' gutterBottom>
                <HistoryIcon sx={{mr: 1, verticalAlign: 'middle'}} />
                Previously Sent To (for this email:{' '}
                {duplicateData?.duplicate_data?.email || duplicateData?.email})
              </Typography>
              <List dense>
                {emailSentHistory.map((history, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <PersonIcon color='primary' />
                    </ListItemIcon>
                    <ListItemText
                      primary={history.user_name || history.customer_name}
                      secondary={`Customer ID: ${history.id}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} orientation='vertical'>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>{getStepContent(index)}</StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions>
        {activeStep < 3 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
            <Button
              variant='contained'
              onClick={activeStep === 2 ? handleSend : handleNext}
              disabled={
                (activeStep === 0 && !selectedDomain) ||
                (activeStep === 1 && selectedCustomers.length === 0) ||
                loading
              }
              startIcon={
                loading ? <CircularProgress size={20} /> : <SendIcon />
              }
            >
              {activeStep === 2 ? 'Send Data' : 'Next'}
            </Button>
          </>
        )}
        {activeStep === 3 && (
          <Button variant='contained' onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

SendToCustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  duplicateData: PropTypes.object,
  onSend: PropTypes.func.isRequired,
  onFetchCustomersByDomain: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  sendingResults: PropTypes.object,
  domains: PropTypes.array,
  customers: PropTypes.array,
  emailSentHistory: PropTypes.array,
  // SSE Progress tracking props
  allStatusCount: PropTypes.number,
  doneStatusCount: PropTypes.number,
  errorStatusCount: PropTypes.number,
  errorData: PropTypes.array,
};

export default SendToCustomerModal;
